import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as AgentsApi from 'api/Agents';
import { openSnackbar } from 'store/reducers/snackbar';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';

const initialState = {
  pagination: {
    pageSize: 10,
    currentPage: 1,
    totalRecords: null,
    index: 1
  },
  data: null,
  fileText: null,
  core: {
    action: null,
    language: null,
    usecase: null,
    voice: null,
    templateValue: null,
    callTypes: null,
    tokens: null,
    did: null,
    emailIntegration: null,
    calIntegration: null,
    calEvents: null
  },
  column: [
    { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
    { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
    { title: 'Country', key: 'country', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
    { title: 'Did', key: 'did', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Usecase', key: 'usecase', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Voice', key: 'voice', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
    { title: 'Call type', key: 'call_type', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
  ],
  status: '',
  ui: {
    loading: false,
    fttLoading: false,
    tokenLoading: false,
    msg: ''
  },
  actions: []
};

const getAgentConfigActionsData = createAsyncThunk('agents/getAgentConfigActionsData', async (params) => {
  const res = await AgentsApi.getAgentConfigActionsData(params);
  return res.data;
});

const getAgentsData = createAsyncThunk('agents/getAgentsData', async ({ params, signal }) => {
  const res = await AgentsApi.getAgentsData(params, signal);
  return res.data?.data;
});

//core action ----------------------------------------------------------------------------------------
const getAgentCoreAction = createAsyncThunk(`agents/getAgentCoreAction`, async ({ slug, signal }) => {
  const res = await AgentsApi.getAgentCoreAction({ slug, signal });
  return res.data?.data;
});
const getAgentCoreLanguage = createAsyncThunk(`agents/getAgentCoreLanguage`, async ({ slug, signal }) => {
  const res = await AgentsApi.getAgentCoreLanguage({ slug, signal });
  return res.data?.data;
});
const getAgentCoreUseCase = createAsyncThunk(`agents/getAgentCoreUseCase`, async ({ slug, signal }) => {
  const res = await AgentsApi.getAgentCoreUseCase({ slug, signal });
  return res.data?.data;
});
const getAgentCoreVoice = createAsyncThunk(`agents/getAgentCoreVoice`, async ({ slug, signal }) => {
  const res = await AgentsApi.getAgentCoreVoice({ slug, signal });
  return res.data?.data;
});

const getAgentCoreCallType = createAsyncThunk('agents/getAgentCoreCallType', async ({ slug, signal }) => {
  const res = await AgentsApi.getAgentCallType({ slug, signal });
  return res.data?.data;
});

const getDid = createAsyncThunk(`agents/getDid`, async (params) => {
  const res = await AgentsApi.getDid(params);
  return res.data?.data;
});

const getAgentTemplateValue = createAsyncThunk(`agents/getAgentTemplateValue`, async (params) => {
  const res = await AgentsApi.getTemplateValue(params);
  return res.data?.data;
});
//----------------------------------------------------------------------------------------------------

const fileToText = createAsyncThunk(`agents/fileToText`, async (params, { rejectWithValue }) => {
  try {
    const res = await AgentsApi.fileToText(params);
    return res.data?.data;
  } catch (error) {
    console.log('caught in error', error);
    return rejectWithValue(error);
  }
});

const getTokens = createAsyncThunk(`agents/getTokens`, async (params, { rejectWithValue }) => {
  try {
    const res = await AgentsApi.getTokens(params);
    return res.data?.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const createUseCase = createAsyncThunk('agents/createUseCase', async (params, { rejectWithValue }) => {
  try {
    const res = await AgentsApi.createUseCase(params);
    return res.data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

const getEmailIntegrationData = createAsyncThunk('agents/getEmailIntegrationData', async (slug) => {
  const res = await AgentsApi.getEmailIntegrationData(slug);
  return res.data?.data;
});

const getCalIntegrationData = createAsyncThunk('agents/getCalIntegrationData', async (slug) => {
  const res = await AgentsApi.getCalIntegrationData(slug);
  return res.data?.data;
});

const getCalEvent = createAsyncThunk('agents/getCalEvent', async (params, { rejectWithValue }) => {
  try {
    const res = await AgentsApi.getCalEvent(params);
    return res.data?.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const chatAgent = createAsyncThunk(`agents/chatAgent`, async (params, { rejectWithValue }) => {
  try {
    const res = await AgentsApi.chatAgent(params);

    return res.data?.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const testAgent = createAsyncThunk(`agents/testAgent`, async (params, { dispatch, rejectWithValue }) => {
  try {
    const res = await AgentsApi.testAgent(params);
    dispatch(
      openSnackbar({
        open: true,
        title: res?.success ? 'Call Created Successfully' : 'Error in creating call',
        message: res?.success ? res?.data?.message : res?.message?.response?.data?.message,
        variant: 'alert',
        alert: {
          color: res?.success ? 'success' : 'error'
        },
        close: false
      })
    );
    return res.data?.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const addAgentActions = createAsyncThunk('agents/addAgentActions', async (params) => {
  try {
    const res = await AgentsApi.addAgentActions(params);
    if (res.success) {
      return { success: true, data: res.data?.data };
    } else {
      dispatch(
        openSnackbar({
          open: true,
          title: 'Error in adding action for agent.',
          message: res?.message?.response?.data?.message,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      return { success: false, error: res.data };
    }
  } catch (error) {
    dispatch(
      openSnackbar({
        open: true,
        title: 'Error in adding action for agent.',
        message: error.response.data?.message,
        variant: 'alert',
        alert: {
          color: 'error'
        },
        close: false
      })
    );
    return rejectWithValue(error);
  }
});

const updateAgentActions = createAsyncThunk(`agents/updateAgentActions`, async (params, { dispatch, rejectWithValue }) => {
  try {
    const res = await AgentsApi.updateAgentActions(params);
    // openSnackbarFunction(res);
    if (res.success) {
      return { success: true, data: res.data?.data };
    } else {
      dispatch(
        openSnackbar({
          open: true,
          title: 'Error in updating action for agent.',
          message: res?.message?.response?.data?.message,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      return { success: false, data: res.data?.data };
    }
  } catch (error) {
    dispatch(
      openSnackbar({
        open: true,
        title: 'Error in deleting action for agent.',
        message: error.response.data?.message,
        variant: 'alert',
        alert: {
          color: 'error'
        },
        close: false
      })
    );
    return rejectWithValue(error);
  }
});

const deleteAgentActions = createAsyncThunk(`agents/deleteAgentActions`, async (params, { dispatch, rejectWithValue }) => {
  try {
    const res = await AgentsApi.deleteAgentActions(params);
    // openSnackbarFunction(res);
    if (!res.success) {
      dispatch(
        openSnackbar({
          open: true,
          title: 'Error in deleting action for agent.',
          message: res?.message?.response?.data?.message,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
    }
  } catch (error) {
    dispatch(
      openSnackbar({
        open: true,
        title: 'Error in deleting action for agent.',
        message: error.response.data?.message,
        variant: 'alert',
        alert: {
          color: 'error'
        },
        close: false
      })
    );
    return rejectWithValue(error);
  }
});

//agent crud
const createAgentData = createAsyncThunk('agents/createAgentData', async (params, { dispatch, rejectWithValue }) => {
  try {
    const res = await AgentsApi.createAgentData(params);
    if (res.success) {
      return { success: true, data: res.data };
    } else {
      dispatch(
        openSnackbar({
          open: true,
          title: 'Error in Creating Agent.',
          message: res?.message?.response?.data?.message,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      return { success: false, error: res.data };
    }
  } catch (err) {
    dispatch(
      openSnackbar({
        open: true,
        message: err.response.data?.message,
        variant: 'alert',
        alert: {
          color: 'error'
        },
        close: false
      })
    );
    return rejectWithValue(err);
  }
});

const deleteAgent = createAsyncThunk('agents/deleteAgent', async (params, { dispatch, rejectWithValue }) => {
  try {
    const res = await AgentsApi.deleteAgent(params);
    if (res.success) {
      return { success: true, data: res.data };
    } else {
      dispatch(
        openSnackbar({
          open: true,
          title: 'Error in Deleting Agent.',
          message: res?.message?.response?.data?.message,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      return { success: false, error: res.data };
    }
  } catch (error) {
    dispatch(
      openSnackbar({
        open: true,
        message: error.response.data?.message,
        variant: 'alert',
        alert: {
          color: 'error'
        },
        close: false
      })
    );
    return rejectWithValue(error);
  }
});

const updateAgent = createAsyncThunk(`agents/updateAgent`, async (params, { dispatch, rejectWithValue }) => {
  try {
    const res = await AgentsApi.updateAgent(params);
    // openSnackbarFunction(res);4
    if (res.success) {
      return { success: true, data: res.data };
    } else {
      dispatch(
        openSnackbar({
          open: true,
          title: 'Error in Updating Agent.',
          message: res?.message?.response?.data?.message,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      return { success: false, error: res.data };
    }
  } catch (error) {
    dispatch(
      openSnackbar({
        open: true,
        message: error.response.data?.message,
        variant: 'alert',
        alert: {
          color: 'error'
        },
        close: false
      })
    );
    return rejectWithValue(error);
  }
});

const agentSlice = createSlice({
  name: 'agents',
  initialState: initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
    },
    setDid: (state, action) => {
      if (action.payload.call_type === 'inbound') {
        state.core.did = [...state.core.did, action.payload.did];
      }
    },
    setChatdata: (state, action) => {
      state.chat = action.payload.userchat;
      state.params = action.payload.paramsobj;
    },
    setClearChatdata: (state) => {
      state.chat = null;
    },
    resetFileText: (state) => {
      state.fileText = null;
      state.core.tokens = null;
    }
  },
  extraReducers: {
    [getAgentsData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getAgentsData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.data = action.payload.results;
      state.pagination.totalRecords = action.payload.count;
    },
    [getAgentsData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },

    [getAgentCoreAction.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getAgentCoreAction.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.action = action.payload;
    },
    [getAgentCoreAction.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getAgentCoreLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getAgentCoreLanguage.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.language = action.payload;
    },
    [getAgentCoreLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getAgentCoreVoice.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getAgentCoreVoice.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.voice = action.payload;
    },
    [getAgentCoreVoice.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getAgentCoreUseCase.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getAgentCoreUseCase.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.usecase = action.payload;
    },
    [getAgentCoreUseCase.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getAgentTemplateValue.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getAgentTemplateValue.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.templateValue = action.payload;
    },
    [getAgentTemplateValue.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getDid.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getDid.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.did = action.payload;
    },
    [getDid.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getAgentCoreCallType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getAgentCoreCallType.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.callTypes = action.payload;
    },
    [getAgentCoreCallType.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [fileToText.pending]: (state) => {
      state.status = 'init';
      state.ui.fttLoading = true;
    },
    [fileToText.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.fttLoading = false;
      state.fileText = action.payload;
    },
    [fileToText.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.fttLoading = false;
      state.error = action.error.message;
    },
    [getTokens.pending]: (state) => {
      state.status = 'init';
      state.ui.tokenLoading = true;
    },
    [getTokens.fulfilled]: (state, action) => {
      state.status = 'init';
      state.ui.tokenLoading = false;
      state.core.tokens = action.payload;
    },
    [getTokens.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.tokenLoading = false;
      state.error = action.error.message;
    },
    [createUseCase.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [createUseCase.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [createUseCase.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getEmailIntegrationData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getEmailIntegrationData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.emailIntegration = action.payload;
    },
    [getEmailIntegrationData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getCalIntegrationData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCalIntegrationData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.calIntegration = action.payload;
    },
    [getCalIntegrationData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getCalEvent.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCalEvent.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.core.calEvents = action.payload;
    },
    [getCalEvent.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [chatAgent.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [chatAgent.fulfilled]: (state, action) => {
      state.status = 'init';
      state.ui.loading = false;
      state.chat = action.payload.chat_data;
    },
    [chatAgent.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [testAgent.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [testAgent.fulfilled]: (state, action) => {
      console.log(action.payload, 'testAgent');
      state.status = 'idle';
      state.ui.loading = false;
    },
    [testAgent.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getAgentConfigActionsData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getAgentConfigActionsData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.actions = action.payload?.data;
    },
    [getAgentConfigActionsData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [addAgentActions.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [addAgentActions.fulfilled]: (state) => {
      state.status = 'init';
      state.ui.loading = false;
    },
    [addAgentActions.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [updateAgentActions.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [updateAgentActions.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [updateAgentActions.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [deleteAgentActions.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [deleteAgentActions.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [deleteAgentActions.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createAgentData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [createAgentData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      console.log('createAgentData', action.payload);
    },
    [createAgentData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [deleteAgent.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [deleteAgent.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [deleteAgent.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [updateAgent.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [updateAgent.fulfilled]: (state) => {
      state.status = 'init';
      state.ui.loading = false;
    },
    [updateAgent.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    }
  }
});

const { setCurrentPage, setPageSize, setDid, resetFileText, setChatdata, setClearChatdata } = agentSlice.actions;

export default agentSlice.reducer;

export {
  setCurrentPage,
  setChatdata,
  setClearChatdata,
  setPageSize,
  getAgentsData,
  getAgentCoreAction,
  getAgentCoreLanguage,
  getAgentCoreCallType,
  getAgentCoreUseCase,
  getAgentCoreVoice,
  getAgentTemplateValue,
  getDid,
  setDid,
  getTokens,
  fileToText,
  resetFileText,
  createUseCase,
  getEmailIntegrationData,
  getCalIntegrationData,
  getCalEvent,
  chatAgent,
  testAgent,
  getAgentConfigActionsData,
  addAgentActions,
  updateAgentActions,
  deleteAgentActions,
  createAgentData,
  deleteAgent,
  updateAgent
};
